import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ButtonCommon } from 'components/common';
import { CaretDown } from '@phosphor-icons/react';
import { useHoverState, getIconWithState } from 'hooks/useIconState';
import { MenuItemType } from 'types/menu.types';

interface SubMenuProps {
  menuItem: MenuItemType;
  isVisible: boolean;
}

const SubMenu: React.FC<SubMenuProps> = ({ menuItem, isVisible }) => {
  const navigate = useNavigate();
  const [activeKeys, setActiveKeys] = useState<Record<string, boolean>>({});
  const { hoveredKey, handleMouseEnter, handleMouseLeave } = useHoverState();

  const submenuAnimationClass = 'transition-all duration-300 ease-in-out transform';

  const handleMenuItemClick = useCallback(
    (item: MenuItemType, e: React.MouseEvent) => {
      e.stopPropagation();

      if (
        item.path &&
        (!item.children ||
          item.children.length === 0 ||
          item.children?.every((child) => child.subMenuDisplay === false))
      ) {
        navigate(item.path);
        return;
      }

      setActiveKeys((prev) => {
        const newState = { ...prev };

        if (newState[item.key]) {
          delete newState[item.key];
        } else {
          newState[item.key] = true;
        }

        return newState;
      });
    },
    [navigate],
  );

  const handleContainerClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
  }, []);

  if (!isVisible || !menuItem.children?.length) {
    return null;
  }

  const renderMenuItems = (items: MenuItemType[], level: number = 0) => {
    return items
      .filter((item) => item.subMenuDisplay !== false)
      .map((item) => {
        const isActive = activeKeys[item.key];
        const isHovered = hoveredKey === item.key;
        const hasChildren = item.children && item.children.length > 0;

        // Check if all children have subMenuDisplay set to false
        const allChildrenHidden = hasChildren && item.children?.every((child) => child.subMenuDisplay === false);

        const icon =
          item.icon && level === 0
            ? getIconWithState(
                item.icon as React.ReactElement,
                isActive,
                isHovered,
                'bold', // Active weight
                'bold', // Hover weight
                'regular', // Default weight
                'var(--primaryColor)',
              )
            : undefined;

        return (
          <div
            className='menu-item-container relative min-w-max'
            key={item.key}
            onMouseEnter={() => handleMouseEnter(item.key)}
            onMouseLeave={handleMouseLeave}
          >
            <ButtonCommon
              onClick={(e) => handleMenuItemClick(item, e as React.MouseEvent)}
              color='none'
              size='sm'
              radius='rounded'
              icon={icon}
              className={`mt-[8px] !text-[14px] w-full !justify-start ${
                isActive ? 'bg-[var(----bgGreyColor4)] font-bold' : ''
              } ${isHovered ? 'hover:bg-[var(--textBlack8)]' : ''}`}
            >
              {hasChildren && !allChildrenHidden ? (
                <div className='w-full flex items-center '>
                  <span className='mr-2'>{item.label}</span>
                  <CaretDown
                    size={16}
                    className={`transition-transform duration-300 ${isActive ? 'rotate-180' : ''}`}
                  />
                </div>
              ) : (
                <span>{item.label}</span>
              )}
            </ButtonCommon>

            {!allChildrenHidden && (
              <div
                className={`menu-children-wrapper overflow-hidden ${submenuAnimationClass}`}
                style={{
                  maxHeight: hasChildren && isActive ? '1000px' : '0px',
                  opacity: hasChildren && isActive ? 1 : 0,
                  transform: hasChildren && isActive ? 'translateY(0)' : 'translateY(-10px)',
                  zIndex: level + 10,
                }}
              >
                <div className={`menu-children ml-4 pl-4 border-l border-gray-100 relative`}>
                  {hasChildren && item.children && renderMenuItems(item.children, level + 1)}
                </div>
              </div>
            )}
          </div>
        );
      });
  };

  return (
    <div
      className='mt-1 bg-white rounded-lg shadow-lg z-50 min-w-[200px] w-fit border border-gray-100 p-3 pt-0'
      onClick={handleContainerClick}
    >
      <div className='flex flex-col py-2 gap-1 w-full'>{renderMenuItems(menuItem.children)}</div>
    </div>
  );
};

export default SubMenu;
